import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useState } from 'react';
import { VerticalCardLoader } from 'syf-component-library/ui/atoms';
import { generateUUID } from 'syf-js-utilities';
import { ConfigContext } from '../../context';
import { useAuthorizedBuyersLookupGet, useAuthorizedUsersGet, useMultiAuthorizedUsersPostMutation } from '../../../../const/syf-api-queries';
import { AuthorizedUsers, CardHoldersFooter, CardHoldersHeader } from '../../main-container/components';
import { BodyContainer, ModalContentWrapper, NoteContainer } from './subcomponents';
import Note from '../../main-container/components/AuthorizedUsers/utils/Note';
import { CardholdersScreen } from '../../typings';
import queryParamsToObject from '../../helpers/query-params-to-object';
import { SAMS_ANALYTICS_PAYLOAD, SAMS_PAGE_NAME } from '../../const/analytics';
import triggerManageCardHoldersSamsAnalytics from '../../helpers/trigger-manage-card-holders-sams-analytics';
import { BUSINESS_ERROR_CODES, RESPONSE_STATUS, STATUS_CODE, validErrorMessageCode } from '../../const/status-and-error-codes';
import displayModalScreen from '../../helpers/display-modal-screen';
import { initialErrorDetails } from '../../const';
import { formatDateToISO } from '../../../../helpers/dateFormatter';
import { useManageCardHolderModalContext } from '../../context/manage-card-holder-modal-context';
const ManageCardHoldersSamsModalContent = ({ lookupRequestData }) => {
    const { config, selectedAccountId, handleClose } = useContext(ConfigContext);
    const [currentScreen, setCurrentScreen] = useState({
        screen: CardholdersScreen.DEFAULT
    });
    const [errorDetails, setErrorDetails] = useState({
        ...initialErrorDetails
    });
    const [isUserVerified, setIsUserVerified] = useState(false);
    const [newAddedAuthorizedUser, setNewAddedAuthorizedUser] = useState();
    const otpErrorCallbackParams = sessionStorage.getItem('otpErrorCallbackParams');
    const { eligibleMembers, selectedSamsUser, updateIsAuthorizedUserSubmitted, updateEligibleMembers } = useManageCardHolderModalContext();
    const { data: authorizedUsersDataGet, isError: isGetAuthorizedError, isFetching: isGetAuthorizedFetching, isSuccess: isGetAuthorizedSuccess, error: getAuthorizedError } = useAuthorizedUsersGet(selectedAccountId);
    const { mutateAsync, isLoading: isPostAuthorizedLoading, error: postAuthorizedError } = useMultiAuthorizedUsersPostMutation(config);
    const remainingAuthUsersAllowed = authorizedUsersDataGet?.remainingAuthUsersAllowed;
    const hasRemainingAuthUsersAllowed = Boolean(remainingAuthUsersAllowed && remainingAuthUsersAllowed > '0');
    const shouldCallLookupApi = isGetAuthorizedSuccess && hasRemainingAuthUsersAllowed;
    const { data: eligibleLookupData, isError: isGetLookupError, isFetching: isGetLookupFetching, error: getLookupError, isSuccess: isGetLookupSuccess } = useAuthorizedBuyersLookupGet({
        lookupRequestData,
        isEnabled: shouldCallLookupApi
    });
    const showLoader = isGetAuthorizedFetching || isPostAuthorizedLoading || isGetLookupFetching;
    const hasEncounteredAPIError = isGetAuthorizedError || isGetLookupError;
    const hasEncounteredError = Object.keys(errorDetails).length > 0 && errorDetails?.hasError;
    const handleResponseError = useCallback(async (error) => {
        const responseStatus = error?.response?.status;
        const isBusinessError = responseStatus === RESPONSE_STATUS.BUSINESS_ERROR;
        const parsedError = isBusinessError ? await error?.response?.json() : null;
        if (responseStatus === RESPONSE_STATUS?.FORBIDDEN && !isUserVerified) {
            setErrorDetails(initialErrorDetails);
            setCurrentScreen({
                screen: CardholdersScreen.VERIFY_USER_SCREEN
            });
            return;
        }
        if (responseStatus && isBusinessError) {
            const { code: businessErrorCode, message: businessErrorDescription } = parsedError.businessErrors[0];
            setErrorDetails({
                hasError: true,
                isBusinessError: true,
                businessErrorCode,
                businessErrorDescription
            });
        }
        else {
            // 4xx, 5xx, or responseStatus is undefined
            setErrorDetails({
                ...initialErrorDetails,
                hasError: true
            });
        }
    }, [isUserVerified]);
    useEffect(() => {
        if (eligibleLookupData && !eligibleMembers?.length) {
            updateEligibleMembers(eligibleLookupData.eligibleMembers.map(member => ({
                ...member,
                'cipher.dateOfBirth': ''
            })));
        }
    }, [eligibleLookupData, updateEligibleMembers, eligibleMembers]);
    useEffect(() => {
        if (hasEncounteredAPIError) {
            const apiError = getAuthorizedError || postAuthorizedError || getLookupError;
            handleResponseError(apiError);
        }
    }, [
        getAuthorizedError,
        handleResponseError,
        hasEncounteredAPIError,
        postAuthorizedError,
        getLookupError
    ]);
    const handleScopeCheck = async () => {
        try {
            await mutateAsync({
                'cipher.accountId': selectedAccountId,
                accountIdType: 'guid',
                authorizedUsers: []
            });
            setIsUserVerified(true);
            setCurrentScreen({ screen: CardholdersScreen.INITIAL_SCREEN });
        }
        catch (error) {
            handleResponseError(error);
        }
    };
    const clearFields = () => {
        setNewAddedAuthorizedUser(undefined);
        updateIsAuthorizedUserSubmitted(false);
    };
    const changeScreen = (screenProps) => {
        setCurrentScreen(screenProps);
        if (hasEncounteredError ||
            ![
                CardholdersScreen.ADD_AUTH_USER_SAMS,
                CardholdersScreen.CONFIRMATION_SCREEN_NEW_USER
            ].includes(screenProps.screen)) {
            clearFields();
        }
    };
    const handleCancel = () => {
        triggerManageCardHoldersSamsAnalytics({
            pageFunction: 'account services',
            pageKind: 'account services'
        });
        handleClose();
        clearFields();
    };
    const handleAddAuthUserSubmit = async () => {
        updateIsAuthorizedUserSubmitted(true);
        try {
            const selectedEligibleMember = selectedSamsUser?.value;
            if (selectedEligibleMember &&
                selectedSamsUser.touched &&
                !selectedSamsUser.errorMessage) {
                const payload = {
                    'cipher.accountId': selectedAccountId,
                    accountIdType: 'guid',
                    authorizedUsers: [
                        {
                            operationId: generateUUID(),
                            'cipher.firstName': selectedEligibleMember?.['cipher.firstName'],
                            'cipher.lastName': selectedEligibleMember?.['cipher.lastName'],
                            'cipher.dateOfBirth': formatDateToISO(selectedEligibleMember?.['cipher.dateOfBirth'] || '01/01/2000')
                        }
                    ]
                };
                const response = await mutateAsync(payload);
                const responseAuthorizedUsers = response?.authorizedUsers;
                if (responseAuthorizedUsers && responseAuthorizedUsers?.length > 0) {
                    const authorizedUser = responseAuthorizedUsers?.[0];
                    const { statusCode, error } = authorizedUser;
                    const code = error?.businessErrors?.[0]?.code;
                    const errorMessage = error?.businessErrors?.[0].message || '';
                    if (statusCode === STATUS_CODE.NEW_USER) {
                        setNewAddedAuthorizedUser({
                            authorizedUsers: payload.authorizedUsers
                        });
                        changeScreen({
                            screen: CardholdersScreen.CONFIRMATION_SCREEN_NEW_USER
                        });
                    }
                    else if (statusCode === STATUS_CODE.EXISTING_USER) {
                        changeScreen({
                            screen: CardholdersScreen.CONFIRMATION_SCREEN_EXISTING_USER
                        });
                    }
                    else if (code && statusCode === STATUS_CODE.BUSINESS_ERROR) {
                        setErrorDetails({
                            hasError: true,
                            isBusinessError: true,
                            businessErrorCode: code,
                            businessErrorDescription: errorMessage
                        });
                    }
                }
            }
        }
        catch (error) {
            handleResponseError(error);
        }
    };
    useEffect(() => {
        if (hasEncounteredError) {
            const businessErrorCode = errorDetails?.businessErrorCode;
            const analyticsError = businessErrorCode && Boolean(SAMS_ANALYTICS_PAYLOAD[businessErrorCode])
                ? SAMS_ANALYTICS_PAYLOAD[businessErrorCode]
                : SAMS_ANALYTICS_PAYLOAD.api_error;
            const errorMappings = {
                [BUSINESS_ERROR_CODES.NO_ELIGIBLE_MEMBERS_FOUND]: {
                    key: 'message',
                    value: 'No eligible members found'
                },
                [BUSINESS_ERROR_CODES.EXPIRED_MEMBERSHIP]: {
                    key: 'errorMessage',
                    value: 'your sam’s club membership has expired'
                },
                [BUSINESS_ERROR_CODES.NOT_ELIGIBLE_MEMBERS]: {
                    key: 'errorMessage',
                    value: 'your sam’s club membership is not eligible for authorized cardholders'
                }
            };
            const errorMapping = businessErrorCode && errorMappings[businessErrorCode];
            triggerManageCardHoldersSamsAnalytics(analyticsError && errorMapping
                ? {
                    ...analyticsError,
                    [errorMapping.key]: errorMapping.value
                }
                : {
                    pageName: SAMS_PAGE_NAME.API_ERROR,
                    errorMessage: 'we’re having trouble retrieving your information'
                });
            setCurrentScreen({
                screen: CardholdersScreen.ERROR_SCREEN,
                customData: {
                    isSamsClient: true,
                    businessErrorCode
                }
            });
        }
    }, [errorDetails, hasEncounteredError]);
    useEffect(() => {
        if (isGetAuthorizedSuccess &&
            isGetLookupSuccess &&
            hasRemainingAuthUsersAllowed &&
            Boolean(!otpErrorCallbackParams)) {
            handleScopeCheck();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isGetAuthorizedSuccess,
        remainingAuthUsersAllowed,
        otpErrorCallbackParams,
        isGetLookupSuccess
    ]);
    useEffect(() => {
        if (otpErrorCallbackParams) {
            const errorObjectParams = queryParamsToObject(otpErrorCallbackParams);
            let stepUpError = '';
            if ('error' in errorObjectParams) {
                stepUpError = errorObjectParams.error;
            }
            if (stepUpError) {
                triggerManageCardHoldersSamsAnalytics({
                    ...SAMS_ANALYTICS_PAYLOAD[stepUpError],
                    errorMessage: SAMS_ANALYTICS_PAYLOAD[stepUpError]?.errorMessage ?? ''
                });
                if (stepUpError === 'user_declined') {
                    setCurrentScreen({ screen: CardholdersScreen.VERIFY_USER_SCREEN });
                }
                if (stepUpError === 'access_denied') {
                    setCurrentScreen({ screen: CardholdersScreen.ACCESS_DENIED_SCREEN });
                }
                else {
                    sessionStorage.removeItem('otpErrorCallbackParams');
                }
            }
        }
    }, [otpErrorCallbackParams]);
    useEffect(() => {
        // Resetting the screen to initial state when the API call is successful
        if (!showLoader &&
            !hasEncounteredError &&
            !otpErrorCallbackParams &&
            currentScreen.screen === CardholdersScreen.DEFAULT) {
            setCurrentScreen({ screen: CardholdersScreen.INITIAL_SCREEN });
        }
    }, [currentScreen, hasEncounteredError, otpErrorCallbackParams, showLoader]);
    if (showLoader) {
        return (_jsx("div", { "data-testid": "skeleton-loader", children: _jsx(VerticalCardLoader, {}) }));
    }
    const addAuthorizedUserScreen = [
        CardholdersScreen.ADD_AUTH_USER_SAMS,
        CardholdersScreen.ADD_AUTH_USER_SAMS_LIST
    ].includes(currentScreen.screen);
    const displayBusinessErrorInInitialScreen = hasEncounteredError &&
        errorDetails?.businessErrorCode &&
        validErrorMessageCode.includes(errorDetails?.businessErrorCode);
    const isNoEligibleMembersFound = hasEncounteredError &&
        errorDetails?.businessErrorCode ===
            BUSINESS_ERROR_CODES.NO_ELIGIBLE_MEMBERS_FOUND;
    const showAuthorizedUser = (!hasEncounteredError ||
        (displayBusinessErrorInInitialScreen && !isNoEligibleMembersFound)) &&
        !addAuthorizedUserScreen;
    const isNewUserConfirmationScreen = currentScreen.screen === CardholdersScreen.CONFIRMATION_SCREEN_NEW_USER;
    const showHeader = Boolean((!hasEncounteredError || Boolean(displayBusinessErrorInInitialScreen)) &&
        !isNewUserConfirmationScreen);
    const shouldDisplayNote = !hasEncounteredError &&
        hasRemainingAuthUsersAllowed &&
        [
            CardholdersScreen.INITIAL_SCREEN,
            CardholdersScreen.VERIFY_USER_SCREEN
        ].includes(currentScreen.screen);
    const isSamsExpired = hasEncounteredError &&
        errorDetails?.businessErrorCode === BUSINESS_ERROR_CODES.EXPIRED_MEMBERSHIP;
    return (_jsxs(ModalContentWrapper, { id: "modal-content-wrapper", children: [_jsxs(BodyContainer, { children: [showHeader && (_jsx(CardHoldersHeader, { isSamsExpired: isSamsExpired, usersLimit: remainingAuthUsersAllowed })), displayModalScreen(currentScreen), showAuthorizedUser && (_jsx(AuthorizedUsers, { authorizedUsers: isNewUserConfirmationScreen
                            ? newAddedAuthorizedUser
                            : authorizedUsersDataGet, currentScreen: currentScreen.screen, remainingAuthUserAllowed: remainingAuthUsersAllowed, isSamsClient: true })), shouldDisplayNote && (_jsx(NoteContainer, { horizontal: "xlarge", children: _jsx(Note, { text: "note" }) }))] }), _jsx(CardHoldersFooter, { isSamsClient: true, handleAddAuthUserSubmit: handleAddAuthUserSubmit, handleClose: handleCancel, changeScreen: changeScreen, currentScreen: currentScreen.screen, usersLimit: remainingAuthUsersAllowed })] }));
};
export default ManageCardHoldersSamsModalContent;
